// Team Watch — the pure rules behind OM / TSM accountability for VA attendance and productivity.
//
// ⛔ PURE BY DESIGN: no prisma, no env, no express. The API sweep, the dashboard read-model and the
// tests all import THIS file, so "what counts as late", "what counts as away" and "what counts as
// under the floor" is one definition, not three that agree today. Anything that touches the clock
// takes `now` as a parameter.
//
// What it decides, in the owner's words (Jacob, 12 Oct 2026): OMs must know — before the client
// does — when a VA has not clocked in, clocked in late, clocked out early, is under 60%
// productivity, or has been away from their laptop for 10 minutes while clocked in.
//
// ⛔ "unknown" is never "bad". Every input that can be missing (no schedule, an agent build that
// does not report input, a web clock-in with no agent) yields NO verdict rather than a guess, because
// an alarm raised from missing data teaches OMs to ignore the alarms.
import { calendarDayKey, midnightOn } from './period.js';
const MIN_MS = 60_000;
// ─────────────────────────── Kinds + thresholds ───────────────────────────
export const TEAM_WATCH_KINDS = ['LATE_CLOCK_IN', 'NO_SHOW', 'EARLY_CLOCK_OUT', 'LOW_PRODUCTIVITY', 'AWAY', 'LOW_ACTIVITY_HOUR', 'LOW_ACTIVITY_TREND'];
/**
 * Kinds that become an incident on the dashboard ONLY — no ticket (Jacob, 21 Oct 2026: "the tickets section is too full and they're missing
 * things"). The OM is told once (a bell alert and a push), sees the row in Needs attention with the same "I'm on it" / "Close" buttons, and
 * anything still open after 48 hours reaches the head manager, the general manager and the directors in the daily digest — not one ticket per VA.
 * Attendance (late, no-show, away, left early) stays a ticket: it is same-day, and a client may notice it first.
 */
export const TEAM_WATCH_TICKETLESS_KINDS = ['LOW_PRODUCTIVITY', 'LOW_ACTIVITY_HOUR', 'LOW_ACTIVITY_TREND'];
/** Whether an incident of this kind raises a ticket. */
export const raisesTicket = (kind) => !TEAM_WATCH_TICKETLESS_KINDS.includes(kind);
export const TEAM_WATCH_KIND_LABEL = {
    LATE_CLOCK_IN: 'Late clock-in',
    NO_SHOW: 'No-show',
    EARLY_CLOCK_OUT: 'Left early',
    LOW_PRODUCTIVITY: 'Low productivity',
    AWAY: 'Away from laptop',
    LOW_ACTIVITY_HOUR: 'Low activity for an hour',
    LOW_ACTIVITY_TREND: 'Low activity, days running',
};
export const TEAM_WATCH_DEFAULTS = {
    lateGraceMinutes: 10,
    noShowMinutes: 30,
    earlyClockOutMinutes: 15,
    awayMinutes: 10,
    productivityMinPct: 60,
    productivityMinSampleMinutes: 90,
    lowActivityPct: 20,
    lowActivityWindowMinutes: 60,
    lowActivityMinReadings: 40,
    lowActivityTrendPct: 40,
    lowActivityTrendDays: 3,
    lowActivityTrendMinReadings: 90,
    ackMinutes: 30,
    trackedWithinDays: 14,
    staleAfterMinutes: 120,
    massAbsenceMin: 5,
    massAbsenceShare: 0.6,
};
// ─────────────────────────── Dates in a zone ───────────────────────────
const pad2 = (n) => String(n).padStart(2, '0');
/** The VA-local calendar day of an instant, 'YYYY-MM-DD'. */
export function localDateKey(d, tz) {
    const k = new Date(calendarDayKey(d, tz));
    return `${k.getUTCFullYear()}-${pad2(k.getUTCMonth() + 1)}-${pad2(k.getUTCDate())}`;
}
function parseDateKey(dateKey) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
    if (!m)
        throw new Error(`bad dateKey: ${dateKey}`);
    return { y: Number(m[1]), mo: Number(m[2]) - 1, d: Number(m[3]) };
}
export function addDaysToDateKey(dateKey, n) {
    const { y, mo, d } = parseDateKey(dateKey);
    const t = new Date(Date.UTC(y, mo, d + n));
    return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}-${pad2(t.getUTCDate())}`;
}
/** 0 = Sunday … 6 = Saturday, of a calendar date (no zone involved — it is a date, not an instant). */
export function dayOfWeekOfDateKey(dateKey) {
    const { y, mo, d } = parseDateKey(dateKey);
    return new Date(Date.UTC(y, mo, d)).getUTCDay();
}
/** The instant of local midnight at the start of `dateKey` in `tz`. */
export function localMidnightOf(dateKey, tz) {
    const { y, mo, d } = parseDateKey(dateKey);
    return midnightOn(y, mo, d, tz);
}
// ─────────────────────────── The expected shift ───────────────────────────
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
function parseHhmm(s) {
    if (!s)
        return null;
    const m = /^(\d{1,2}):(\d{2})$/.exec(s.trim());
    if (!m)
        return null;
    const h = Number(m[1]);
    const mi = Number(m[2]);
    if (h > 23 || mi > 59)
        return null;
    return h * 60 + mi;
}
/**
 * The expected shift for a weekday from every schedule the VA carries (their own, and one per
 * client). A VA rostered for several clients is due at the EARLIEST start and is done at the LATEST
 * end; the hours they owe are the UNION of the intervals, so two clients' overlapping hours are not
 * counted twice.
 */
export function expectedShiftFor(schedules, dow) {
    const key = DAY_KEYS[dow];
    if (!key)
        return { kind: 'NO_SCHEDULE' };
    let hasSchedule = false;
    const intervals = [];
    for (const sch of schedules) {
        if (!sch?.days)
            continue;
        hasSchedule = true;
        const day = sch.days[key];
        if (!day?.working)
            continue;
        const start = parseHhmm(day.start);
        const end = parseHhmm(day.end);
        if (start == null || end == null)
            continue;
        intervals.push([start, end <= start ? end + 1440 : end]);
    }
    if (!hasSchedule)
        return { kind: 'NO_SCHEDULE' };
    if (!intervals.length)
        return { kind: 'OFF_DAY' };
    intervals.sort((a, b) => a[0] - b[0]);
    const [firstStart, firstEnd] = intervals[0];
    let scheduled = 0;
    let curS = firstStart;
    let curE = firstEnd;
    for (const [s, e] of intervals.slice(1)) {
        if (s <= curE)
            curE = Math.max(curE, e);
        else {
            scheduled += curE - curS;
            curS = s;
            curE = e;
        }
    }
    scheduled += curE - curS;
    return { kind: 'WORKING', startMinute: firstStart, endMinute: Math.max(...intervals.map((i) => i[1])), scheduledMinutes: scheduled };
}
/** The absolute start/end instants of a WORKING shift on `dateKey` (the date the shift STARTS on). */
export function shiftInstants(dateKey, shift, tz) {
    const midnight = localMidnightOf(dateKey, tz).getTime();
    return { startAt: new Date(midnight + shift.startMinute * MIN_MS), endAt: new Date(midnight + shift.endMinute * MIN_MS) };
}
/**
 * Median first-clock-in minute-of-day, for a VA with NO explicit schedule. Needs ≥ 2 prior working
 * days — with less there is no pattern, so no verdict (the same rule the hourly R50 sweep used).
 */
export function medianStartMinute(firstClockInMinutes) {
    if (firstClockInMinutes.length < 2)
        return null;
    const s = [...firstClockInMinutes].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return s.length % 2 ? s[mid] : Math.round((s[mid - 1] + s[mid]) / 2);
}
const overlaps = (s, from, to, now) => (s.end ?? now).getTime() > from.getTime() && s.start.getTime() < to.getTime();
/**
 * Did they clock in on time? A span already running when the shift starts is ON TIME (they clocked
 * in early). `LATE` / `NO_SHOW` are the LIVE states — still not in; `CLOCKED_IN_LATE` is the closed
 * record of one that came in after the grace.
 */
export function startVerdict(a) {
    const overlapping = a.spans.filter((s) => overlaps(s, a.startAt, a.endAt, a.now));
    if (overlapping.length) {
        const first = overlapping.reduce((m, s) => (s.start.getTime() < m.start.getTime() ? s : m));
        const late = Math.max(0, Math.floor((first.start.getTime() - a.startAt.getTime()) / MIN_MS));
        return { state: late > a.th.lateGraceMinutes ? 'CLOCKED_IN_LATE' : 'ON_TIME', lateMinutes: late, clockedInAt: first.start };
    }
    const m = Math.floor((a.now.getTime() - a.startAt.getTime()) / MIN_MS);
    if (m <= a.th.lateGraceMinutes)
        return { state: 'NOT_DUE', lateMinutes: Math.max(0, m), clockedInAt: null };
    return { state: m >= a.th.noShowMinutes ? 'NO_SHOW' : 'LATE', lateMinutes: m, clockedInAt: null };
}
/**
 * Did they stay to the end? Judged only once the shift is over by the settle window (a clock-out is
 * not final while they can still come back), and only when the hours were NOT made up — someone who
 * started early and put in the full day did not "leave early".
 */
export function endVerdict(a) {
    const winFrom = new Date(a.startAt.getTime() - 3 * 60 * MIN_MS);
    const winTo = new Date(a.endAt.getTime() + 3 * 60 * MIN_MS);
    const overlapping = a.spans.filter((s) => overlaps(s, winFrom, winTo, a.now) && overlaps(s, a.startAt, a.endAt, a.now));
    if (!overlapping.length)
        return { state: 'NO_CLOCK_IN', earlyMinutes: 0, lastClockOutAt: null, workedMinutes: 0 };
    let worked = 0;
    for (const s of a.spans) {
        if (!overlaps(s, winFrom, winTo, a.now))
            continue;
        const from = Math.max(s.start.getTime(), winFrom.getTime());
        const to = Math.min((s.end ?? a.now).getTime(), winTo.getTime(), a.now.getTime());
        worked += Math.max(0, to - from);
    }
    const workedMinutes = Math.floor(worked / MIN_MS);
    if (overlapping.some((s) => s.end == null)) {
        return { state: a.now.getTime() >= a.endAt.getTime() ? 'STILL_CLOCKED_IN' : 'NOT_DUE', earlyMinutes: 0, lastClockOutAt: null, workedMinutes };
    }
    const lastOut = overlapping.reduce((m, s) => (s.end.getTime() > m.getTime() ? s.end : m), overlapping[0].end);
    if (a.now.getTime() < a.endAt.getTime() + a.th.earlyClockOutMinutes * MIN_MS)
        return { state: 'NOT_DUE', earlyMinutes: 0, lastClockOutAt: lastOut, workedMinutes };
    const early = Math.floor((a.endAt.getTime() - lastOut.getTime()) / MIN_MS);
    const madeUp = workedMinutes >= a.scheduledMinutes - a.th.earlyClockOutMinutes;
    if (early > a.th.earlyClockOutMinutes && !madeUp)
        return { state: 'LEFT_EARLY', earlyMinutes: early, lastClockOutAt: lastOut, workedMinutes };
    return { state: 'FULL_SHIFT', earlyMinutes: Math.max(0, early), lastClockOutAt: lastOut, workedMinutes };
}
const minuteKey = (d) => Math.floor(d.getTime() / MIN_MS);
/**
 * One sample per minute. The screenshot and the app-activity beacon come from the same agent tick, so
 * they would otherwise count twice; prefer the reading that carries `idleSeconds`, then any that has
 * an activity figure, then whatever is left.
 */
export function dedupeByMinute(samples) {
    const rank = (s) => (s.idleSeconds != null ? 2 : s.activityPct != null ? 1 : 0);
    const byMinute = new Map();
    for (const s of samples) {
        const k = minuteKey(s.at);
        const cur = byMinute.get(k);
        if (!cur || rank(s) > rank(cur))
            byMinute.set(k, s);
    }
    return [...byMinute.values()].sort((a, b) => a.at.getTime() - b.at.getTime());
}
/** Samples that fall inside a clocked-in, agent-monitored span. */
export function samplesInSpans(samples, spans, now) {
    const monitored = spans.filter((s) => s.monitored);
    return samples.filter((x) => monitored.some((s) => x.at.getTime() >= s.start.getTime() && x.at.getTime() <= (s.end ?? now).getTime()));
}
/** Mean of the agent's activity figure over the sampled minutes (samples with no figure are skipped, not zeroed). */
export function productivity(samples) {
    const usable = dedupeByMinute(samples).filter((s) => s.activityPct != null);
    if (!usable.length)
        return { pct: null, sampleMinutes: 0 };
    const sum = usable.reduce((t, s) => t + s.activityPct, 0);
    return { pct: Math.round(sum / usable.length), sampleMinutes: usable.length };
}
/** Under the floor, AND enough minutes sampled to say so. */
export function isLowProductivity(p, th) {
    return p.pct != null && p.sampleMinutes >= th.productivityMinSampleMinutes && p.pct < th.productivityMinPct;
}
/**
 * Stretches of at least `lowActivityWindowMinutes` of clocked-in time in which the VA's keyboard/mouse activity averaged under
 * `lowActivityPct` — the "really low activity for an hour" a client notices and an audit then finds.
 *
 * It is NOT the away rule: away is no input at all for 10 minutes; this is a VA who is at the laptop and barely working. A VA away for
 * an hour will trip both, which is right (two different things to say), and the ticket for this one is a single ticket per day.
 *
 * ⛔ Only agent-monitored spans are read (a browser clock-in sends nothing), only readings that carry an activity figure count (a missing
 * figure is unknown, not zero), and a window needs `lowActivityMinReadings` of its minutes to have a reading — missing data never
 * reads as low activity. A window must lie wholly inside ONE clocked-in span, so a VA's lunch (clocked out) is never counted.
 */
export function detectLowActivityEpisodes(samples, spans, now, th) {
    if (!(th.lowActivityPct > 0))
        return [];
    const windowMs = th.lowActivityWindowMinutes * MIN_MS;
    const nowMs = now.getTime();
    const out = [];
    for (const span of spans) {
        if (!span.monitored)
            continue;
        const spanStart = span.start.getTime();
        const spanEnd = (span.end ?? now).getTime();
        const inSpan = dedupeByMinute(samples.filter((x) => x.activityPct != null && x.at.getTime() >= spanStart && x.at.getTime() <= spanEnd));
        // A window ends at each reading and covers the hour before it.
        const lowWindows = [];
        let lo = 0;
        let sum = 0;
        for (let hi = 0; hi < inSpan.length; hi++) {
            const end = inSpan[hi].at.getTime();
            sum += inSpan[hi].activityPct;
            while (lo < hi && inSpan[lo].at.getTime() <= end - windowMs) {
                sum -= inSpan[lo].activityPct;
                lo++;
            }
            if (end - spanStart < windowMs)
                continue; // not yet a full hour of clocked-in time
            const n = hi - lo + 1;
            if (n >= th.lowActivityMinReadings && sum / n < th.lowActivityPct)
                lowWindows.push({ s: end - windowMs, e: end });
        }
        // Windows that overlap or touch are one stretch.
        let cur = null;
        const flush = () => {
            if (!cur)
                return;
            const inside = inSpan.filter((x) => x.at.getTime() >= cur.s && x.at.getTime() <= cur.e);
            const mean = inside.reduce((t, x) => t + x.activityPct, 0) / Math.max(1, inside.length);
            out.push({
                startedAt: new Date(cur.s),
                endedAt: new Date(cur.e),
                minutes: Math.floor((cur.e - cur.s) / MIN_MS),
                pct: Math.round(mean),
                ongoing: span.end == null && nowMs - cur.e <= LOW_ACTIVITY_FRESH_MS,
            });
        };
        for (const w of lowWindows) {
            if (cur && w.s <= cur.e)
                cur.e = Math.max(cur.e, w.e);
            else {
                flush();
                cur = { s: w.s, e: w.e };
            }
        }
        flush();
    }
    return out.sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
}
const LOW_ACTIVITY_FRESH_MS = 150_000; // a reading this recent means the stretch is still live
/**
 * The VA's last `lookbackDays` COMPLETED local days that were working days, oldest first. `todayKey` is never included: a day still being
 * worked proves nothing yet.
 *
 * A working day is one the schedule says is worked; with no schedule at all it is Monday–Friday. A day with fewer than `minReadings` readings
 * is `pct: null` — unknown, never low. A day the VA took off is not here at all (it neither extends nor breaks a run); a working day with no
 * readings IS here as unknown, so it breaks one: three "low" days with a day we know nothing about in between are not three days running.
 */
export function trendDays(input) {
    const out = [];
    for (let back = input.lookbackDays; back >= 1; back--) {
        const dateKey = addDaysToDateKey(input.todayKey, -back);
        const dow = dayOfWeekOfDateKey(dateKey);
        const expected = expectedShiftFor(input.schedules, dow);
        const working = expected.kind === 'WORKING' || (expected.kind === 'NO_SCHEDULE' && dow >= 1 && dow <= 5);
        if (!working)
            continue;
        const got = input.byDate.get(dateKey);
        const readings = got?.readings ?? 0;
        out.push({ dateKey, readings, pct: got && got.pct != null && readings >= input.minReadings ? got.pct : null });
    }
    return out;
}
/**
 * Working days running under the line, counted back from the newest judged day, or null when the run is shorter than `lowActivityTrendDays`.
 * ⛔ A day that is unknown (null) or at/above the line ends the run: missing data is never a low day, and the line is strict (`<`).
 */
export function detectLowActivityTrend(days, th) {
    if (!(th.lowActivityTrendPct > 0) || !(th.lowActivityTrendDays >= 1))
        return null;
    const run = [];
    for (let i = days.length - 1; i >= 0; i--) {
        const d = days[i];
        if (d.pct == null || d.pct >= th.lowActivityTrendPct)
            break;
        run.unshift(d);
    }
    if (run.length < th.lowActivityTrendDays)
        return null;
    const avg = run.reduce((t, d) => t + d.pct, 0) / run.length;
    return { days: run.length, startDateKey: run[0].dateKey, endDateKey: run[run.length - 1].dateKey, avgPct: Math.round(avg), dateKeys: run.map((d) => d.dateKey) };
}
/**
 * How many one-a-minute readings with an activity figure the last `lowActivityWindowMinutes` hold. The low-activity rule can only judge a VA
 * with `lowActivityMinReadings` of them, so this is the difference between "nobody is under the line" and "nobody is being measured".
 */
export function recentReadingCount(samples, now, th) {
    const nowMs = now.getTime();
    const from = nowMs - th.lowActivityWindowMinutes * MIN_MS;
    return dedupeByMinute(samples.filter((x) => x.activityPct != null && x.at.getTime() > from && x.at.getTime() <= nowMs)).length;
}
const AGENT_WINDOW_MS = 10 * MIN_MS; // the agent's trailing activity window
const MERGE_SLOP_MS = 90_000; // ticks are a minute apart; touching segments are one episode
const FRESH_MS = 150_000; // a reading this recent means the episode is still live
/**
 * Stretches where a clocked-in VA was not at their laptop for at least `awayMinutes`.
 *
 * Two kinds of evidence, merged into one timeline per span:
 *   - INPUT: a reading says "no keyboard/mouse input" — `idleSeconds` when the agent sends it
 *     (exact), else `activityPct === 0`, which means no input across the agent's whole trailing
 *     window (≤10 min, clamped to the time since clock-in so a fresh shift cannot look idle at minute 2).
 *   - SIGNAL: no reading at all for `awayMinutes` — laptop closed, asleep, agent crashed, offline.
 *
 * ⛔ Only agent-monitored spans are examined. A browser clock-in sends no readings at all, and
 * reading that silence as "away" would flag every web session. A reading whose figures are both null
 * is unknown and adds no evidence either way.
 */
export function detectAwayEpisodes(samples, spans, now, th) {
    const awayMs = th.awayMinutes * MIN_MS;
    const nowMs = now.getTime();
    const out = [];
    for (const span of spans) {
        if (!span.monitored)
            continue;
        const spanStart = span.start.getTime();
        const spanEnd = (span.end ?? now).getTime();
        if (spanEnd - spanStart < awayMs)
            continue;
        const inSpan = dedupeByMinute(samples.filter((s) => s.at.getTime() >= spanStart && s.at.getTime() <= spanEnd));
        const segs = [];
        for (const smp of inSpan) {
            const t = smp.at.getTime();
            if (smp.idleSeconds != null) {
                if (smp.idleSeconds > 0)
                    segs.push({ s: Math.max(spanStart, t - smp.idleSeconds * 1000), e: t, kind: 'INPUT' });
            }
            else if (smp.activityPct === 0) {
                const w = Math.min(AGENT_WINDOW_MS, t - spanStart);
                if (w > 0)
                    segs.push({ s: t - w, e: t, kind: 'INPUT' });
            }
        }
        let prev = spanStart;
        for (const smp of inSpan) {
            const t = smp.at.getTime();
            if (t - prev >= awayMs)
                segs.push({ s: prev, e: t, kind: 'SIGNAL' });
            prev = t;
        }
        if (spanEnd - prev >= awayMs)
            segs.push({ s: prev, e: spanEnd, kind: 'SIGNAL' });
        segs.sort((a, b) => a.s - b.s);
        let cur = null;
        const flush = () => {
            if (!cur || cur.e - cur.s < awayMs)
                return;
            const ongoing = span.end == null && nowMs - cur.e <= FRESH_MS;
            out.push({
                startedAt: new Date(cur.s),
                endedAt: new Date(cur.e),
                minutes: Math.floor((cur.e - cur.s) / MIN_MS),
                ongoing,
                cause: cur.signal > cur.input ? 'NO_SIGNAL' : 'NO_INPUT',
            });
        };
        for (const g of segs) {
            const len = g.e - g.s;
            if (cur && g.s <= cur.e + MERGE_SLOP_MS) {
                cur.e = Math.max(cur.e, g.e);
                if (g.kind === 'INPUT')
                    cur.input += len;
                else
                    cur.signal += len;
            }
            else {
                flush();
                cur = { s: g.s, e: g.e, input: g.kind === 'INPUT' ? len : 0, signal: g.kind === 'SIGNAL' ? len : 0 };
            }
        }
        flush();
    }
    return out.sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
}
// ─────────────────────────── Coverage ───────────────────────────
/**
 * Is this VA on RT Hub's clock at all? A VA still tracked in another tool has no RT Hub data, and
 * judging them "late" every morning would drown the real alarms. They are shown as NOT TRACKED — a
 * visible state, never a silent green.
 */
export function isTrackedHere(lastClockInAt, clockedInNow, now, th) {
    if (clockedInNow)
        return true;
    if (!lastClockInAt)
        return false;
    return now.getTime() - lastClockInAt.getTime() <= th.trackedWithinDays * 24 * 60 * MIN_MS;
}
/** A holiday or an outage looks like a mass no-show. Hold individual incidents and tell directors once. */
export function isMassAbsence(flagged, scheduledTracked, th) {
    if (scheduledTracked <= 0)
        return false;
    return flagged >= th.massAbsenceMin && flagged / scheduledTracked >= th.massAbsenceShare;
}
// ─────────────────────────── Live status (the dashboard's one word per VA) ───────────────────────────
export const VA_LIVE_STATUSES = [
    'NO_SHOW',
    'LATE',
    'NO_SIGNAL',
    'AWAY',
    'LEFT_EARLY',
    'PAST_SHIFT_END',
    'WORKING',
    'ON_BREAK',
    'CLOCKED_OUT',
    'NOT_STARTED',
    'FINISHED',
    'NO_SCHEDULE',
    'OFF_TODAY',
    'ON_LEAVE',
    'NOT_TRACKED',
];
/** Worst first — the order an OM should read the list in. */
export const VA_LIVE_STATUS_RANK = Object.fromEntries(VA_LIVE_STATUSES.map((s, i) => [s, i]));
export const VA_LIVE_STATUS_LABEL = {
    NO_SHOW: 'No-show',
    LATE: 'Late — not in',
    NO_SIGNAL: 'No signal',
    AWAY: 'Away from laptop',
    LEFT_EARLY: 'Left early',
    PAST_SHIFT_END: 'Still on past shift end',
    WORKING: 'Working',
    ON_BREAK: 'On break',
    CLOCKED_OUT: 'Clocked out',
    NOT_STARTED: 'Not started yet',
    FINISHED: 'Finished',
    NO_SCHEDULE: 'No shift schedule',
    OFF_TODAY: 'Day off',
    ON_LEAVE: 'On leave',
    NOT_TRACKED: 'Not on RT Hub clock',
};
export function liveStatus(a) {
    if (!a.tracked)
        return 'NOT_TRACKED';
    if (a.clockedIn) {
        if (a.away)
            return a.away.cause === 'NO_SIGNAL' ? 'NO_SIGNAL' : 'AWAY';
        return a.minutesPastEnd > 60 ? 'PAST_SHIFT_END' : 'WORKING';
    }
    if (a.onLeave)
        return 'ON_LEAVE';
    if (a.onBreak)
        return 'ON_BREAK';
    if (a.expected.kind === 'NO_SCHEDULE')
        return a.clockedToday ? 'FINISHED' : 'NO_SCHEDULE';
    if (a.expected.kind === 'OFF_DAY')
        return a.clockedToday ? 'FINISHED' : 'OFF_TODAY';
    // A working day, not clocked in right now.
    if (!a.clockedToday) {
        if (a.start?.state === 'NO_SHOW')
            return 'NO_SHOW';
        if (a.start?.state === 'LATE')
            return 'LATE';
        return 'NOT_STARTED';
    }
    if (a.end?.state === 'LEFT_EARLY')
        return 'LEFT_EARLY';
    if (a.end?.state === 'FULL_SHIFT')
        return 'FINISHED';
    return 'CLOCKED_OUT';
}
// ─────────────────────────── OM response statistics ───────────────────────────
/**
 * How a director closes out an incident nobody answered for a week (the "close stale" press). It is not an answer and not an approval: `acknowledgedAt` stays
 * empty, so the miss still counts against the OM's reply rate, and it is not APPROVED, so the day stays on the VA's record. It only takes the incident, and its
 * ticket, off the open lists.
 */
export const TEAM_WATCH_STALE_RESOLUTION = 'STALE';
/** An incident still open this many days after it was detected is stale. */
export const TEAM_WATCH_STALE_AFTER_DAYS = 7;
function median(nums) {
    if (!nums.length)
        return null;
    const s = [...nums].sort((a, b) => a - b);
    const mid = Math.floor(s.length / 2);
    return Math.round(s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2);
}
const minutesBetween = (a, b) => Math.max(0, Math.round((b.getTime() - a.getTime()) / MIN_MS));
/**
 * How one OM (or all of them) is doing at dealing with what was raised. ⛔ An UNACKNOWLEDGED incident
 * counts against `ackWithinWindowPct` the moment its window has passed — an OM cannot improve their
 * numbers by never opening the ticket.
 */
export function responseStats(rows, th, now) {
    const acks = rows.filter((r) => r.acknowledgedAt).map((r) => minutesBetween(r.detectedAt, r.acknowledgedAt));
    // ⛔ A STALE close is a director's tidy-up, not the OM fixing anything: counting its 10 days would make the OM's time-to-resolve look like the director's schedule.
    const resolves = rows.filter((r) => r.resolvedAt && r.resolution !== TEAM_WATCH_STALE_RESOLUTION).map((r) => minutesBetween(r.detectedAt, r.resolvedAt));
    const judged = rows.filter((r) => r.acknowledgedAt != null || now.getTime() - r.detectedAt.getTime() > th.ackMinutes * MIN_MS);
    const within = judged.filter((r) => r.acknowledgedAt != null && minutesBetween(r.detectedAt, r.acknowledgedAt) <= th.ackMinutes).length;
    return {
        total: rows.length,
        open: rows.filter((r) => !r.resolvedAt).length,
        unacknowledged: rows.filter((r) => !r.acknowledgedAt).length,
        escalated: rows.filter((r) => r.escalatedAt).length,
        medianAckMinutes: median(acks),
        medianResolveMinutes: median(resolves),
        ackWithinWindowPct: judged.length ? Math.round((within / judged.length) * 100) : null,
    };
}
//# sourceMappingURL=teamWatch.js.map